import { __ } from '@wordpress/i18n';
import { createYearSelect } from './yearSelect';

const REST_BASE = '/wp-json/anacrm/v1';

function nonce() {
	return window.anacrm?.nonce ?? '';
}

async function apiFetch( url, options = {} ) {
	const res = await fetch( url, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce(),
			...( options.headers ?? {} ),
		},
	} );
	if ( res.status === 204 ) {
		return null;
	}
	const body = await res.json().catch( () => ( {} ) );
	if ( ! res.ok ) {
		// eslint-disable-next-line no-console
		console.error( '[solvency] apiFetch error', res.status, url, body );
		throw new Error( body?.message ?? `HTTP ${ res.status }` );
	}
	return body;
}

/**
 * Reads the currently selected term IDs from the org form's `_or_field`
 * Pods multi-autocomplete widget, live from the DOM. Same technique as
 * setupAnagosMembershipVisibility() in orgFormAccordion.js for `_or_network`.
 * @param {HTMLElement|null} orgFieldWrapper
 */
function getSelectedFieldIds( orgFieldWrapper ) {
	if ( ! orgFieldWrapper ) {
		return [];
	}
	return [
		...orgFieldWrapper.querySelectorAll(
			'input[type="hidden"][name^="pods_field__or_field"]'
		),
	]
		.map( ( el ) => parseInt( el.value, 10 ) )
		.filter( ( n ) => ! isNaN( n ) );
}

async function fetchFieldTerms( ids ) {
	if ( ! ids.length ) {
		return [];
	}
	const res = await fetch(
		`/wp-json/wp/v2/field?include=${ ids.join(
			','
		) }&_fields=id,name&per_page=100`,
		{ headers: { 'X-WP-Nonce': nonce() } }
	);
	if ( ! res.ok ) {
		return [];
	}
	return res.json();
}

// GET responses use camelCase ids; pending (not-yet-persisted) entries use
// the snake_case REST payload keys — normalise reads across both shapes.
const extractFieldId = ( item ) => item.field_id ?? item.fieldId ?? null;

function solvencyLabel( item ) {
	const billing = Number( item.billing ?? 0 ).toLocaleString( 'es-ES', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	} );
	if ( 'technical' === item.type ) {
		return `${ item.fieldName || __( '(no category)', 'anacrm' ) } — ${
			item.year
		} — ${ billing }€`;
	}
	return `${ item.year } — ${ billing }€`;
}

function renderRow( container, item, onDelete, onEdit ) {
	const row = document.createElement( 'div' );
	row.className = 'flex items-center gap-2 py-1 solvency-row';
	row.dataset.id = item.id ?? '';

	const label = document.createElement( 'span' );
	label.className = 'flex-1 text-sm';
	label.textContent = solvencyLabel( item );

	const edit = document.createElement( 'button' );
	edit.type = 'button';
	edit.className =
		'px-2 py-0.5 text-sm text-gray-600 hover:text-gray-900 cursor-pointer shrink-0';
	edit.textContent = __( 'Edit', 'anacrm' );
	edit.addEventListener( 'click', () => onEdit( row, label, item ) );

	const del = document.createElement( 'button' );
	del.type = 'button';
	del.className =
		'px-2 py-0.5 text-sm text-red-600 hover:text-red-800 cursor-pointer shrink-0';
	del.textContent = __( 'Remove', 'anacrm' );
	del.addEventListener( 'click', () => onDelete( row, item ) );

	row.appendChild( label );
	row.appendChild( edit );
	row.appendChild( del );
	container.appendChild( row );
}

/**
 * Mount the "Solvency for Public Procurement" repeater into `container`.
 * Two subsections — financial solvency (year + billing) and technical
 * solvency (category + year + billing, category restricted to the org's
 * own `_or_field` selections) — each with its own add/edit/delete flow.
 * Follows the same isNew/pending-hidden-input pattern as
 * initOrgMembershipSection() in membershipRepeater.js.
 *
 * @param {HTMLElement}      container       Mount point (inside the org form).
 * @param {string}           orgId           Post ID string, empty for new-org form.
 * @param {boolean}          isNew           True when creating a new org.
 * @param {HTMLElement|null} orgFieldWrapper The `_or_field` Pods field wrapper.
 */
export async function initOrgSolvencySection(
	container,
	orgId,
	isNew,
	orgFieldWrapper
) {
	const intro = document.createElement( 'p' );
	intro.className = 'text-sm mb-3';
	intro.textContent = __(
		'We ask for Financial Solvency data (Annual Business Turnover) and Technical Solvency data (Billing by activity type), for the last 3 years. To contract with the Public Sector, tender specifications usually require having billed, in at least one of the last 3 years, a minimum of X euros. To identify whether you can opt for public contracts (reserved or not), this information is essential, especially for the Observatory for Responsible Public Procurement of the Canary Islands.',
		'anacrm'
	);
	container.appendChild( intro );

	// Pending list for new-org form (not yet persisted). Shared between the
	// financial and technical subsections, each entry tagged by `type`.
	let pending = [];

	const hiddenInput = document.createElement( 'input' );
	hiddenInput.type = 'hidden';
	hiddenInput.name = 'anacrm_pending_solvencies';
	hiddenInput.value = '[]';
	container.appendChild( hiddenInput );

	function syncHidden() {
		hiddenInput.value = JSON.stringify( pending );
	}

	let items = [];
	if ( ! isNew && orgId ) {
		try {
			const fetched = await apiFetch(
				`${ REST_BASE }/org/${ orgId }/solvencies`
			);
			if ( Array.isArray( fetched ) ) {
				items = fetched;
			}
		} catch {
			// Non-fatal — show empty lists.
		}
	}

	async function handleDelete( row, item ) {
		if ( ! item.id ) {
			return;
		}
		try {
			await apiFetch( `${ REST_BASE }/solvencies/${ item.id }`, {
				method: 'DELETE',
			} );
			row.remove();
		} catch {
			const e = document.createElement( 'span' );
			e.className = 'text-xs text-red-600 ml-1';
			e.textContent = __( 'Error', 'anacrm' );
			row.appendChild( e );
		}
	}

	function handleDeletePending( row, item ) {
		pending = pending.filter( ( p ) => p !== item );
		syncHidden();
		row.remove();
	}

	// --- Financial solvency -------------------------------------------

	const financialWrap = document.createElement( 'div' );
	financialWrap.className = 'mb-6';
	container.appendChild( financialWrap );

	const financialLabel = document.createElement( 'p' );
	financialLabel.className = 'text-sm font-medium mb-1';
	financialLabel.textContent = __(
		'What has been, approximately, your annual business turnover (€)?',
		'anacrm'
	);
	financialWrap.appendChild( financialLabel );

	const financialList = document.createElement( 'div' );
	financialList.className = 'solvency-list mb-3 space-y-1';
	financialWrap.appendChild( financialList );

	for ( const item of items.filter( ( i ) => 'financial' === i.type ) ) {
		renderRow( financialList, item, handleDelete, startEditFinancial );
	}

	const financialAddArea = document.createElement( 'div' );
	financialWrap.appendChild( financialAddArea );

	const financialAddBtn = document.createElement( 'button' );
	financialAddBtn.type = 'button';
	financialAddBtn.className =
		'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
	financialAddBtn.textContent = __( '+ Add financial solvency', 'anacrm' );
	financialAddArea.appendChild( financialAddBtn );

	const financialForm = document.createElement( 'div' );
	financialForm.className =
		'hidden flex flex-wrap gap-2 mt-2 items-end solvency-add-form';
	financialAddArea.appendChild( financialForm );

	const financialYear = createYearSelect(
		'border border-gray-300 rounded px-2 py-1 text-sm w-24'
	);

	const financialBilling = document.createElement( 'input' );
	financialBilling.type = 'number';
	financialBilling.min = '0';
	financialBilling.step = '0.01';
	financialBilling.placeholder = __( 'Billing (€)', 'anacrm' );
	financialBilling.className =
		'border border-gray-300 rounded px-2 py-1 text-sm w-32';

	const financialSave = document.createElement( 'button' );
	financialSave.type = 'button';
	financialSave.className =
		'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
	financialSave.textContent = __( 'Save', 'anacrm' );

	const financialCancel = document.createElement( 'button' );
	financialCancel.type = 'button';
	financialCancel.className =
		'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
	financialCancel.textContent = __( 'Cancel', 'anacrm' );

	const financialErr = document.createElement( 'p' );
	financialErr.className = 'w-full text-xs text-red-600 hidden';

	financialForm.appendChild( financialYear );
	financialForm.appendChild( financialBilling );
	financialForm.appendChild( financialSave );
	financialForm.appendChild( financialCancel );
	financialForm.appendChild( financialErr );

	let financialEditing = null;

	function startEditFinancial( row, label, item ) {
		financialEditing = { row, label, item };
		financialAddBtn.classList.add( 'hidden' );
		financialForm.classList.remove( 'hidden' );
		financialYear.value = item.year ?? '';
		financialBilling.value = item.billing ?? '';
		financialErr.classList.add( 'hidden' );
		financialSave.textContent = __( 'Update', 'anacrm' );
	}

	function stopEditFinancial() {
		financialEditing = null;
		financialYear.value = '';
		financialBilling.value = '';
		financialForm.classList.add( 'hidden' );
		financialAddBtn.classList.remove( 'hidden' );
		financialSave.textContent = __( 'Save', 'anacrm' );
	}

	financialAddBtn.addEventListener( 'click', () => {
		financialAddBtn.classList.add( 'hidden' );
		financialForm.classList.remove( 'hidden' );
		financialYear.value = '';
		financialBilling.value = '';
		financialErr.classList.add( 'hidden' );
	} );

	financialCancel.addEventListener( 'click', stopEditFinancial );

	financialSave.addEventListener( 'click', async () => {
		const year = financialYear.value.trim();
		const billing = financialBilling.value.trim();

		if (
			! /^\d{4}$/.test( year ) ||
			'' === billing ||
			isNaN( Number( billing ) )
		) {
			financialErr.textContent = __(
				'Please enter a valid year and billing amount.',
				'anacrm'
			);
			financialErr.classList.remove( 'hidden' );
			return;
		}

		financialErr.classList.add( 'hidden' );
		financialSave.disabled = true;

		if ( financialEditing ) {
			const { label, item } = financialEditing;
			if ( item.id ) {
				try {
					const updated = await apiFetch(
						`${ REST_BASE }/solvencies/${ item.id }`,
						{
							method: 'PUT',
							body: JSON.stringify( {
								org_id: parseInt( orgId, 10 ),
								type: 'financial',
								year,
								billing: Number( billing ),
							} ),
						}
					);
					Object.assign( item, updated );
					label.textContent = solvencyLabel( item );
					stopEditFinancial();
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.error( '[solvency] update failed', err );
					financialErr.textContent =
						err.message ||
						__( 'Could not save. Please try again.', 'anacrm' );
					financialErr.classList.remove( 'hidden' );
				} finally {
					financialSave.disabled = false;
				}
			} else {
				item.year = year;
				item.billing = Number( billing );
				syncHidden();
				label.textContent = solvencyLabel( item );
				stopEditFinancial();
				financialSave.disabled = false;
			}
			return;
		}

		if ( isNew ) {
			const entry = {
				type: 'financial',
				year,
				billing: Number( billing ),
			};
			pending.push( entry );
			syncHidden();
			renderRow(
				financialList,
				entry,
				handleDeletePending,
				startEditFinancial
			);
			stopEditFinancial();
			financialSave.disabled = false;
		} else {
			try {
				const created = await apiFetch( `${ REST_BASE }/solvencies`, {
					method: 'POST',
					body: JSON.stringify( {
						org_id: parseInt( orgId, 10 ),
						type: 'financial',
						year,
						billing: Number( billing ),
					} ),
				} );
				renderRow(
					financialList,
					created,
					handleDelete,
					startEditFinancial
				);
				stopEditFinancial();
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( '[solvency] save failed', err );
				financialErr.textContent =
					err.message ||
					__( 'Could not save. Please try again.', 'anacrm' );
				financialErr.classList.remove( 'hidden' );
			} finally {
				financialSave.disabled = false;
			}
		}
	} );

	// --- Technical solvency ---------------------------------------------

	const technicalWrap = document.createElement( 'div' );
	container.appendChild( technicalWrap );

	const technicalLabel = document.createElement( 'p' );
	technicalLabel.className = 'text-sm font-medium mb-1';
	technicalLabel.textContent = __(
		'Approximate billing for each activity, by year, is requested.',
		'anacrm'
	);
	technicalWrap.appendChild( technicalLabel );

	const technicalList = document.createElement( 'div' );
	technicalList.className = 'solvency-list mb-3 space-y-1';
	technicalWrap.appendChild( technicalList );

	for ( const item of items.filter( ( i ) => 'technical' === i.type ) ) {
		renderRow( technicalList, item, handleDelete, startEditTechnical );
	}

	const technicalAddArea = document.createElement( 'div' );
	technicalWrap.appendChild( technicalAddArea );

	const technicalAddBtn = document.createElement( 'button' );
	technicalAddBtn.type = 'button';
	technicalAddBtn.className =
		'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
	technicalAddBtn.textContent = __( '+ Add technical solvency', 'anacrm' );
	technicalAddArea.appendChild( technicalAddBtn );

	const technicalWarning = document.createElement( 'p' );
	technicalWarning.className = 'hidden text-xs text-red-600 mt-2';
	technicalWarning.textContent = __(
		'Select at least one category in "Category" above before adding technical solvency.',
		'anacrm'
	);
	technicalAddArea.appendChild( technicalWarning );

	const technicalForm = document.createElement( 'div' );
	technicalForm.className =
		'hidden flex flex-wrap gap-2 mt-2 items-end solvency-add-form';
	technicalAddArea.appendChild( technicalForm );

	const technicalCategory = document.createElement( 'select' );
	technicalCategory.className =
		'border border-gray-300 rounded px-2 py-1 text-sm';

	const technicalYear = createYearSelect(
		'border border-gray-300 rounded px-2 py-1 text-sm w-24'
	);

	const technicalBilling = document.createElement( 'input' );
	technicalBilling.type = 'number';
	technicalBilling.min = '0';
	technicalBilling.step = '0.01';
	technicalBilling.placeholder = __( 'Billing (€)', 'anacrm' );
	technicalBilling.className =
		'border border-gray-300 rounded px-2 py-1 text-sm w-32';

	const technicalSave = document.createElement( 'button' );
	technicalSave.type = 'button';
	technicalSave.className =
		'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
	technicalSave.textContent = __( 'Save', 'anacrm' );

	const technicalCancel = document.createElement( 'button' );
	technicalCancel.type = 'button';
	technicalCancel.className =
		'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
	technicalCancel.textContent = __( 'Cancel', 'anacrm' );

	const technicalErr = document.createElement( 'p' );
	technicalErr.className = 'w-full text-xs text-red-600 hidden';

	technicalForm.appendChild( technicalCategory );
	technicalForm.appendChild( technicalYear );
	technicalForm.appendChild( technicalBilling );
	technicalForm.appendChild( technicalSave );
	technicalForm.appendChild( technicalCancel );
	technicalForm.appendChild( technicalErr );

	let technicalEditing = null;

	function populateCategorySelect( terms, selectedId ) {
		technicalCategory.innerHTML = '';
		for ( const t of terms ) {
			const opt = document.createElement( 'option' );
			opt.value = String( t.id );
			opt.textContent = t.name;
			if ( selectedId && t.id === selectedId ) {
				opt.selected = true;
			}
			technicalCategory.appendChild( opt );
		}
	}

	function setTechnicalFormEnabled( enabled ) {
		technicalCategory.disabled = ! enabled;
		technicalYear.disabled = ! enabled;
		technicalBilling.disabled = ! enabled;
		technicalSave.disabled = ! enabled;
	}

	function startEditTechnical( row, label, item ) {
		technicalEditing = { row, label, item };
		technicalAddBtn.classList.add( 'hidden' );
		technicalWarning.classList.add( 'hidden' );
		technicalForm.classList.remove( 'hidden' );
		technicalYear.value = item.year ?? '';
		technicalBilling.value = item.billing ?? '';
		technicalErr.classList.add( 'hidden' );
		technicalSave.textContent = __( 'Update', 'anacrm' );

		const currentFieldId = extractFieldId( item );
		const ids = getSelectedFieldIds( orgFieldWrapper );

		populateCategorySelect(
			currentFieldId
				? [ { id: currentFieldId, name: item.fieldName ?? '' } ]
				: [],
			currentFieldId
		);
		setTechnicalFormEnabled( false );

		fetchFieldTerms( ids ).then( ( terms ) => {
			// Keep the row's own category selectable even if it's no longer
			// among the org's currently selected `_or_field` terms.
			const merged =
				currentFieldId &&
				! terms.some( ( t ) => t.id === currentFieldId )
					? [
							...terms,
							{ id: currentFieldId, name: item.fieldName ?? '' },
					  ]
					: terms;
			populateCategorySelect( merged, currentFieldId );
			setTechnicalFormEnabled( true );
		} );
	}

	function stopEditTechnical() {
		technicalEditing = null;
		technicalYear.value = '';
		technicalBilling.value = '';
		technicalForm.classList.add( 'hidden' );
		technicalWarning.classList.add( 'hidden' );
		technicalAddBtn.classList.remove( 'hidden' );
		technicalSave.textContent = __( 'Save', 'anacrm' );
	}

	technicalAddBtn.addEventListener( 'click', async () => {
		technicalAddBtn.classList.add( 'hidden' );
		technicalForm.classList.remove( 'hidden' );
		technicalYear.value = '';
		technicalBilling.value = '';
		technicalErr.classList.add( 'hidden' );

		const ids = getSelectedFieldIds( orgFieldWrapper );

		if ( ! ids.length ) {
			technicalWarning.classList.remove( 'hidden' );
			populateCategorySelect( [], null );
			setTechnicalFormEnabled( false );
			return;
		}

		technicalWarning.classList.add( 'hidden' );
		setTechnicalFormEnabled( false );

		const terms = await fetchFieldTerms( ids );

		if ( ! terms.length ) {
			technicalWarning.classList.remove( 'hidden' );
			populateCategorySelect( [], null );
			return;
		}

		populateCategorySelect( terms, null );
		setTechnicalFormEnabled( true );
	} );

	technicalCancel.addEventListener( 'click', stopEditTechnical );

	technicalSave.addEventListener( 'click', async () => {
		const fieldId = parseInt( technicalCategory.value, 10 );
		const year = technicalYear.value.trim();
		const billing = technicalBilling.value.trim();
		const fieldName =
			technicalCategory.options[ technicalCategory.selectedIndex ]
				?.textContent ?? '';

		if (
			! fieldId ||
			! /^\d{4}$/.test( year ) ||
			'' === billing ||
			isNaN( Number( billing ) )
		) {
			technicalErr.textContent = __(
				'Please select a category and enter a valid year and billing amount.',
				'anacrm'
			);
			technicalErr.classList.remove( 'hidden' );
			return;
		}

		technicalErr.classList.add( 'hidden' );
		technicalSave.disabled = true;

		if ( technicalEditing ) {
			const { label, item } = technicalEditing;
			if ( item.id ) {
				try {
					const updated = await apiFetch(
						`${ REST_BASE }/solvencies/${ item.id }`,
						{
							method: 'PUT',
							body: JSON.stringify( {
								org_id: parseInt( orgId, 10 ),
								type: 'technical',
								year,
								billing: Number( billing ),
								field_id: fieldId,
							} ),
						}
					);
					Object.assign( item, updated );
					label.textContent = solvencyLabel( item );
					stopEditTechnical();
				} catch ( err ) {
					// eslint-disable-next-line no-console
					console.error( '[solvency] update failed', err );
					technicalErr.textContent =
						err.message ||
						__( 'Could not save. Please try again.', 'anacrm' );
					technicalErr.classList.remove( 'hidden' );
				} finally {
					technicalSave.disabled = false;
				}
			} else {
				item.year = year;
				item.billing = Number( billing );
				item.field_id = fieldId;
				item.fieldName = fieldName;
				syncHidden();
				label.textContent = solvencyLabel( item );
				stopEditTechnical();
				technicalSave.disabled = false;
			}
			return;
		}

		if ( isNew ) {
			const entry = {
				type: 'technical',
				year,
				billing: Number( billing ),
				field_id: fieldId,
				fieldName,
			};
			pending.push( entry );
			syncHidden();
			renderRow(
				technicalList,
				entry,
				handleDeletePending,
				startEditTechnical
			);
			stopEditTechnical();
			technicalSave.disabled = false;
		} else {
			try {
				const created = await apiFetch( `${ REST_BASE }/solvencies`, {
					method: 'POST',
					body: JSON.stringify( {
						org_id: parseInt( orgId, 10 ),
						type: 'technical',
						year,
						billing: Number( billing ),
						field_id: fieldId,
					} ),
				} );
				renderRow(
					technicalList,
					created,
					handleDelete,
					startEditTechnical
				);
				stopEditTechnical();
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( '[solvency] save failed', err );
				technicalErr.textContent =
					err.message ||
					__( 'Could not save. Please try again.', 'anacrm' );
				technicalErr.classList.remove( 'hidden' );
			} finally {
				technicalSave.disabled = false;
			}
		}
	} );
}
